import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import logo from "../../assets/images/logo.svg";
import { logout } from "../../actions/auth";
import { hardLogoutOrenda } from "./orendaUserManager";
import { clearOrendaAuth } from "./orendaAuthStorage";

// Left navigation rail for Orenda cardholders — mirrors the classic Navigation
// visual language (sidebar with logo, nav items, logout footer) but with its
// own component so we can grow it independently without touching the old nav.
//
// The dashboard uses an internal view switcher instead of real routing so
// nav clicks don't lose the loaded application state; `onNavigate(viewKey)`
// bubbles up to OrendaDashboard which swaps the right pane. If no handler
// is supplied the items fall back to legacy router Links.

const OrendaNav = ({ activePath = "dashboard", onNavigate }) => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const signOutEverything = () => {
    try { clearOrendaAuth(); } catch { /* ignore */ }
    dispatch(logout());
    hardLogoutOrenda();
  };

  const name = currentUser?.user?.Name || "";
  const clientLogo = currentUser?.user?.ClientLogoUrl;

  return (
    <div className="col-lg-3 col-md-0 bo-sidebar-col orenda-nav" id="leftNav">
      <div className="bo-sidebar-outer">
        <Link to="/orenda-landing" className="orenda-nav-logo">
          <div className="orenda-nav-wordmark">
            <span className="orenda-nav-wordmark-bo">bo</span>
            <span className="orenda-nav-wordmark-payments">payments</span>
          </div>
        </Link>

        <nav className="orenda-nav-items">
          <NavEntry
            viewKey="dashboard"
            label="Dashboard"
            iconKind="dashboard"
            fallbackTo="/orenda-landing"
            activePath={activePath}
            onNavigate={onNavigate}
          />
          <NavEntry
            viewKey="beneficiaries"
            label="Beneficiaries"
            iconKind="beneficiaries"
            fallbackTo="/orenda-landing"
            activePath={activePath}
            onNavigate={onNavigate}
          />
          <NavEntry
            viewKey="payments"
            label="Payments"
            iconKind="payments"
            fallbackTo="/orenda-landing"
            activePath={activePath}
            onNavigate={onNavigate}
          />
          <NavEntry
            viewKey="transactions"
            label="Transactions"
            iconKind="transactions"
            fallbackTo="/orenda-landing"
            activePath={activePath}
            onNavigate={onNavigate}
          />
          <NavEntry
            viewKey="3ds"
            label="3D Secure"
            iconKind="shield"
            fallbackTo="/orenda-landing"
            activePath={activePath}
            onNavigate={onNavigate}
          />
          {/* Future: Settings / Help — add here. */}
        </nav>

        <div className="orenda-nav-footer">
          <button
            type="button"
            className="orenda-nav-logout"
            onClick={signOutEverything}
          >
            <NavIcon kind="logout" />
            <div>
              <div className="orenda-nav-logout-label">Logout</div>
              {name && <div className="orenda-nav-logout-name">{name}</div>}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Nav row that routes via onNavigate when the dashboard provides one, or
// falls back to a react-router Link when the parent doesn't own the view
// switcher (e.g. a direct URL deep-link that lands on OrendaLanding).
const NavEntry = ({
  viewKey,
  label,
  iconKind,
  fallbackTo,
  activePath,
  onNavigate,
}) => {
  const active = activePath === viewKey;
  const cls = `orenda-nav-item ${active ? "orenda-nav-item--active" : ""}`;
  if (typeof onNavigate === "function") {
    return (
      <button
        type="button"
        className={cls}
        onClick={() => onNavigate(viewKey)}
        aria-current={active ? "page" : undefined}
      >
        <NavIcon kind={iconKind} />
        <span>{label}</span>
      </button>
    );
  }
  return (
    <Link to={fallbackTo} className={cls} aria-current={active ? "page" : undefined}>
      <NavIcon kind={iconKind} />
      <span>{label}</span>
    </Link>
  );
};

const NavIcon = ({ kind }) => {
  const common = {
    width: 20,
    height: 20,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (kind === "dashboard") {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    );
  }
  if (kind === "beneficiaries") {
    // Address-book / people glyph — reads as "the people I pay".
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="10" cy="7" r="4" />
        <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M17 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (kind === "payments") {
    // Arrow-in-circle glyph — "money moving out".
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M12 2v20" />
        <path d="M17 7l-5-5-5 5" />
        <circle cx="12" cy="16" r="6" />
      </svg>
    );
  }
  if (kind === "transactions") {
    // Two arrows — one up, one down — reading as "money in / money out".
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M7 10l4-4 4 4" />
        <line x1="11" y1="6" x2="11" y2="18" />
        <path d="M17 14l-4 4-4-4" />
        <line x1="13" y1="18" x2="13" y2="6" />
      </svg>
    );
  }
  if (kind === "shield") {
    // Shield + checkmark — reads as "secure authorisation / approval".
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M12 2l8 4v6c0 5-3.5 9.4-8 10-4.5-.6-8-5-8-10V6l8-4z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  }
  if (kind === "logout") {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    );
  }
  return null;
};

export default OrendaNav;
